import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { RealtimeStatusMessage } from '@ngsos/portal-core/portal';

import { toTime } from '@ngsos/portal-core/time';

import { WarningBanner } from '../banners/WarningBanner';

export const FIRST_CONNECT_GRACE_MS = 3_000;

export type RealtimeConnectionStatusBannerProps = {
  message: RealtimeStatusMessage;
};

export function RealtimeConnectionStatusBanner({ message }: RealtimeConnectionStatusBannerProps) {
  const { t, i18n } = useTranslation();
  const firstConnectIsSlow = useElapsed(message.kind === 'connecting', FIRST_CONNECT_GRACE_MS);

  if (message.kind === 'offline') {
    return (
      <WarningBanner>
        <span className="w-100 text-center">{t('HubConnectionStatus_Offline')}</span>
      </WarningBanner>
    );
  }

  if (message.kind === 'connecting') {
    return firstConnectIsSlow ? <WarningBanner>{t('HubConnectionStatus_Connecting')}</WarningBanner> : null;
  }

  if (message.kind !== 'disconnected') {
    return null;
  }

  return (
    <WarningBanner>
      {t('HubConnectionStatus_LastLiveData', {
        time: toTime(message.disconnectedSince, i18n.language || 'en'),
      })}
    </WarningBanner>
  );
}

function useElapsed(running: boolean, delayMs: number): boolean {
  const [elapsed, setElapsed] = useState(false);

  useEffect(() => {
    if (!running) {
      setElapsed(false);
      return;
    }

    const timer = setTimeout(() => setElapsed(true), delayMs);

    return () => clearTimeout(timer);
  }, [running, delayMs]);

  return elapsed;
}
